<template>
  <Autocomplete
    v-model="internalValue"
    :disabled="true"
    :search="searchTemplates"
    :get-result-value="getResultValue"
    :placeholder="props.placeholder"
    :defaultValue="undefined"
    v-if="props.tree"
    :clearable="true"
    :close-on-select="true">
    <template
      #default="{ rootProps, inputProps, inputListeners, resultListProps, resultListListeners, results, loading }">
      <div v-bind="rootProps" :key="closeCounter">
        <div class="input-icon mb-2">
          <input v-bind="inputProps" v-on="inputListeners" ref="inputRef" type="text" class="form-control fw-bold" />
          <span class="input-icon-addon">
            <div v-if="loading" class="spinner-border spinner-border-sm text-muted" role="status"></div>
            <button
              v-else-if="modelValue"
              type="button"
              class="flex items-center justify-center p-0 border-0 bg-transparent cursor-pointer"
              style="pointer-events: auto"
              @click.stop="clearValue">
              <IconX :size="16" />
            </button>
            <IconSearch v-else :size="16" />
          </span>
        </div>
        <div v-bind="resultListProps" @mousedown="resultListListeners.mousedown">
          <BaseTree :modelValue="results" treeLine updateBehavior="new" :treeLineOffset="22">
            <template #default="{ node }">
              <SplitAllocationItem
                :leftLabel="node.name"
                :class="[hasSufficientBudget(node) ? 'split-item' : 'disabled-split-item']"
                @click="hasSufficientBudget(node) ? (internalValue = node) : null">
                <template #rightLabel>
                  <small class="m-0 text-muted">
                    {{ formatCurrency(getRemainingBudget(node)) }}
                    <span class="fw-normal">{{ $t('of') }} {{ formatCurrency(node.total_budget) }}</span></small
                  >
                </template>
              </SplitAllocationItem>
            </template>
          </BaseTree>
        </div>
      </div>
    </template>
  </Autocomplete>
</template>

<script setup lang="ts">
import { BaseTree } from '@he-tree/vue';
import '@he-tree/vue/style/default.css';

import { Autocomplete } from '@prospective/lithium';
import { IconSearch, IconX } from '@tabler/icons-vue';
import { computed, ref } from 'vue';
import SplitAllocationItem from './SplitAllocationItem.vue';
import { formatCurrency } from '@/locale/format';
import type { IBudgetTreeNode } from '@/types/budget';
import type { IProductVBudget } from '@/types/product';
import { useActiveBudgetStore } from '@/stores/activeBudget.ts';

const activeBudgetStore = useActiveBudgetStore();

const modelValue = defineModel<IProductVBudget>();

const props = withDefaults(
  defineProps<{
    tree: IBudgetTreeNode;
    placeholder?: string;
    expectedBudget?: number | null;
  }>(),
  {
    placeholder: 'Select a split',
    expectedBudget: null,
  }
);

const tempAutocompleteValue = ref<string>('');
const closeCounter = ref<number>(0);

/* -------------------------------- COMPUTED -------------------------------- */
const internalValue = computed({
  get(): string {
    if (modelValue.value?.name) return modelValue.value.name;
    return tempAutocompleteValue.value;
  },
  set(value: IProductVBudget) {
    if (typeof value === 'object') {
      modelValue.value = value;
      tempAutocompleteValue.value = '';
      closeCounter.value++; // Force close autocomplete dropdown
    } else {
      tempAutocompleteValue.value = value;
      modelValue.value = undefined as any;
    }
  },
});

/* --------------------------------- METHODS -------------------------------- */
function searchTemplates(searchedKey: string): Promise<IProductVBudget[]> {
  return new Promise((resolve) => {
    if (!props.tree) return resolve([]);

    const output: IProductVBudget[] = [];
    const { id, name, currency } = props.tree.customer_budget!.data;
    const { available_initial, available_unallocated } = props.tree.availability;
    output.push({
      id,
      name,
      currency,
      type: 'customer_budget',
      total_budget: available_initial,
      remaining_budget: available_unallocated,
      children: [],
    });
    output[0].children = props.tree.children.map(mapToDropdown);

    return resolve(filterNodes(output, searchedKey));
  });
}

function filterNodes(nodes: IProductVBudget[], searchedKey: string): IProductVBudget[] {
  return nodes.filter((node) => {
    const matchesSearch = node.name.toLowerCase().includes(searchedKey.toLowerCase());
    const filteredChildren = filterNodes(node.children || [], searchedKey);

    if (matchesSearch) {
      return true;
    }

    if (filteredChildren.length > 0) {
      node.children = filteredChildren;
      return true;
    }

    return false;
  });
}

function mapToDropdown(node: IBudgetTreeNode): IProductVBudget | null {
  // Decide which data to take depending on type
  const data = node.vbudget?.data;
  const availability = node.availability;
  if (!data) return null;

  const total_budget = availability.available_initial ?? 0;
  const remaining_budget = availability.available_unallocated ?? 0;

  return {
    id: data.id ?? -1, // fallback if missing
    name: data.name ?? 'Unknown',
    type: node.type, //vbudget type
    currency: data.allocation_currency ?? 'CHF',
    total_budget,
    remaining_budget,
    children: node.children.map(mapToDropdown),
  };
}

function getResultValue(value: any) {
  return value.name;
}

function getRemainingBudget(node: IProductVBudget) {
  const remaining = activeBudgetStore.getRemainingBudgetAfterReserved(
    node.type === 'vbudget' ? node.id : undefined,
    node.remaining_budget
  );
  return remaining;
}

function hasSufficientBudget(node: IProductVBudget) {
  if (props.expectedBudget === null) return true;
  const remaining = activeBudgetStore.getRemainingBudgetAfterReserved(
    node.type === 'vbudget' ? node.id : undefined,
    node.remaining_budget
  );
  return remaining >= props.expectedBudget;
}

function clearValue() {
  modelValue.value = undefined as any;
  tempAutocompleteValue.value = '';
  closeCounter.value++;
}
</script>
<style scoped>
:deep(.vtlist .tree-node[aria-posinset='1'] > .tree-vline:first-child) {
  margin-top: -8px;
}

:deep(.vtlist .tree-vline),
:deep(.vtlist .tree-hline) {
  background-color: rgba(0, 0, 0, 0.1);
}

.split-item {
  cursor: pointer;
  margin: 8px 12px;
}

.disabled-split-item {
  cursor: default;
  background-color: rgba(0, 0, 0, 0.1);
  margin: 8px 12px;
}

.split-item:hover {
  background-color: rgba(0, 0, 0, 0.1);
}
</style>
